// crewbit-admin/src/lib/moneyRpc.ts
//
// Canonical wrapper for every Money RPC invocation from the admin dashboard.
// Spec: docs/references/MONEY_MODULE_WIRING_v1.md §2.1.
//
// Identical contract to crewbit-mobile/lib/moneyRpc.ts:
// - Hooks call this wrapper exclusively. INV-4 lint enforces no direct supabase.rpc(...)
//   calls outside this file.
// - Wrapper does NOT manipulate args. Hooks own snake_case + p_idempotency_key inclusion.
// - Strict response-shape guard. Anything without success: true|false discriminator
//   is treated as UNKNOWN error and Sentry-captured.
// - alreadyExists flag normalized from various per-RPC flag names.
//
// Sentry import differs from mobile (@sentry/react v10 vs @sentry/react-native v7).

import * as Sentry from '@sentry/react';
import { supabase } from './supabase';
import { mapPostgresError, type MoneyError } from './errorMap';

export type MoneyRpcResult<T> =
  | { success: true; data: T; alreadyExists: boolean }
  | { success: false; error: MoneyError };

/**
 * Invoke a Money RPC. Returns a discriminated union — never throws on business errors.
 *
 * @param rpcName  the snake_case RPC name as declared in DATA spec §5
 * @param args     RPC arguments matching the function's declared signature exactly
 */
export async function callMoneyRpc<TResult>(
  rpcName: string,
  args: Record<string, unknown>
): Promise<MoneyRpcResult<TResult>> {
  const { data, error } = await supabase.rpc(rpcName, args);

  if (error) {
    return { success: false, error: mapPostgresError(error) };
  }

  if (data == null || typeof data !== 'object' || Array.isArray(data) || !('success' in data)) {
    Sentry.captureMessage(`Malformed RPC response from ${rpcName}`, {
      tags: { rpcName },
      extra: { received: data },
    });
    return {
      success: false,
      error: { code: 'UNKNOWN', message: `RPC ${rpcName} returned unexpected shape` },
    };
  }

  if ((data as { success: false; error?: MoneyError }).success === false) {
    const errPayload = (data as { error?: MoneyError }).error;
    if (!errPayload || typeof errPayload !== 'object' || !('code' in errPayload)) {
      return {
        success: false,
        error: { code: 'UNKNOWN', message: `RPC ${rpcName} returned success:false without error payload` },
      };
    }
    return { success: false, error: errPayload };
  }

  const d = data as Record<string, unknown>;
  const alreadyExists = Boolean(
    d.already_created ||
    d.already_paid ||
    d.already_processed ||
    d.already_cancelled ||
    d.already_voided ||
    d.already_written_off
  );

  return {
    success: true,
    data: data as unknown as TResult,
    alreadyExists,
  };
}
